import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Filter, SortAsc, Grid, List, AlignLeft, ChevronRight, ChevronDown, Home } from 'lucide-react';
import Header from '../components/Header';
import ProductCard from '../components/ProductCard';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

const CategoryPage = ({ category, onBack, onCategoryChange }) => {
  const { t } = useTranslation();
  const { currentTenant } = useAuth();
  const { getCurrentBranding } = useBranding();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('order');
  const [viewMode, setViewMode] = useState('detailed');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [productsData, categoriesData] = await Promise.all([
          apiService.getProducts(),
          apiService.getCategories()
        ]);
        setProducts(productsData || []);
        setCategories(categoriesData || []);
      } catch (error) {
        console.error('Error loading data:', error);
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentTenant, category.id]); // Recargar cuando cambie el tenant o la categoría

  const getImageUrl = (icon) => {
    const defaultImage = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=400&fit=crop";
    if (!icon) return defaultImage;
    if (icon.startsWith('http')) return icon;
    return `${apiService.baseURL}${icon}`;
  };

  const handleImageError = (e) => {
    e.target.src = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=400&fit=crop";
  };

  // Filtrar solo productos activos para la vista pública
  const activeProducts = products.filter(product => product.is_active);

  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Calcular subcategorías de esta categoría con useMemo
  const subcategories = useMemo(() => {
    return categories.filter(cat => cat.parent_id === category.id);
  }, [category.id, categories.length]);
  
  // Reset subcategoría seleccionada cuando cambia la categoría
  // Si hay subcategorías, seleccionar automáticamente la primera
  useEffect(() => {
    if (subcategories.length > 0) {
      setSelectedSubcategory(subcategories[0].id);
    } else {
      setSelectedSubcategory(null);
    }
    // Scroll al inicio cuando cambia la categoría
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category.id, subcategories.length]);
  
  // Scroll al inicio cuando cambia la subcategoría
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedSubcategory]);
  
  // Filtrar productos de la categoría padre y sus subcategorías
  const categoryProducts = activeProducts.filter(product => {
    if (selectedSubcategory) {
      // Verificar si el producto está en la subcategoría seleccionada (principal o adicional)
      const isInSubcategory = product.category_id === selectedSubcategory ||
        (product.additionalCategories && product.additionalCategories.some(cat => cat.id === selectedSubcategory));
      return isInSubcategory;
    }
    // Si no hay subcategoría seleccionada, mostrar productos de la categoría y todas sus subcategorías
    const subcategoryIds = subcategories.map(sub => sub.id);
    const allCategoryIds = [category.id, ...subcategoryIds];
    
    // Verificar si el producto está en la categoría principal
    const isInMainCategory = allCategoryIds.includes(product.category_id);
    
    // Verificar si el producto está en alguna categoría adicional
    const isInAdditionalCategories = product.additionalCategories && 
      product.additionalCategories.some(cat => allCategoryIds.includes(cat.id));
    
    return isInMainCategory || isInAdditionalCategories;
  });

  const filteredProducts = categoryProducts.filter(product => {
    const searchLower = searchTerm.toLowerCase();
    
    // Buscar en nombre y descripción del producto
    const matchesProduct = 
      product.name.toLowerCase().includes(searchLower) ||
      product.description.toLowerCase().includes(searchLower);
    
    // Buscar en el nombre de la categoría del producto
    const productCategory = categories.find(cat => cat.id === product.category_id);
    const matchesCategory = productCategory?.name.toLowerCase().includes(searchLower);
    
    // Buscar en el nombre de la categoría padre si el producto está en una subcategoría
    const parentCategory = productCategory?.parent_id 
      ? categories.find(cat => cat.id === productCategory.parent_id)
      : null;
    const matchesParentCategory = parentCategory?.name.toLowerCase().includes(searchLower);
    
    return matchesProduct || matchesCategory || matchesParentCategory;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price':
        return a.price - b.price;
      case 'lastUpdated': // Ordenar por fecha de última actualización
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'order':
      default:
        // Ordenar por category_id primero, luego por order_index, luego por nombre
        if (a.category_id !== b.category_id) {
          return a.category_id.localeCompare(b.category_id);
        }
        const orderDiff = (a.order_index || 0) - (b.order_index || 0);
        return orderDiff !== 0 ? orderDiff : a.name.localeCompare(b.name);
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="flex max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 gap-6">
        {/* Sidebar con árbol de categorías */}
        <motion.aside
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="hidden lg:block w-80 flex-shrink-0"
        >
          <div className="sticky top-24 bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <AlignLeft className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t('category.title')}</h3>
                <p className="text-xs text-gray-500">Navegación rápida</p>
              </div>
            </div>

            {/* Botón para volver al home */}
            <button
              onClick={onBack}
              className="w-full flex items-center gap-3 px-4 py-3 mb-4 text-white rounded-xl hover:shadow-lg transition-all duration-200 shadow-md font-medium"
              style={{
                backgroundColor: getCurrentBranding().buttonBackColor || '#10B981',
                hover: {
                  backgroundColor: `${getCurrentBranding().buttonBackColor || '#10B981'}dd`
                }
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonBackColor || '#10B981'}dd`}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonBackColor || '#10B981'}
            >
              <Home className="w-5 h-5" />
              {t('common.back')}
            </button>

            {/* Árbol de categorías */}
            <div className="space-y-2">
              {categories
                .filter(cat => !cat.parent_id)
                .map((cat) => {
                  const catSubcategories = subcategories.filter(sub => sub.parent_id === cat.id);
                  const isCurrentCategory = cat.id === category.id;
                  
                  return (
                    <div key={cat.id} className="space-y-1">
                      <button
                        onClick={() => onCategoryChange && onCategoryChange(cat)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 ${
                          isCurrentCategory
                            ? 'text-white shadow-md'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                        style={isCurrentCategory ? {
                          backgroundColor: getCurrentBranding().buttonCategorySelectedColor || '#3B82F6'
                        } : {}}
                      >
                        <span className="flex-1 font-medium text-sm">{cat.name}</span>
                      </button>

                      {/* Mostrar subcategorías solo si es la categoría actual */}
                      {isCurrentCategory && catSubcategories.length > 0 && (
                        <div className="ml-4 space-y-1 border-l-2 border-blue-200 pl-3">
                          {catSubcategories.map(sub => {
                            const subProducts = activeProducts.filter(p => p.category_id === sub.id).length;
                            return (
                              <button
                                key={sub.id}
                                onClick={() => setSelectedSubcategory(sub.id)}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-sm ${
                                  selectedSubcategory === sub.id
                                    ? 'bg-purple-100 text-purple-700 font-medium'
                                    : 'hover:bg-gray-50 text-gray-600'
                                }`}
                              >
                                <span className="flex-1">{sub.name}</span>
                                <span className="text-xs bg-white/50 px-2 py-0.5 rounded-full">
                                  {subProducts}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

              {/* Botón Marketing Kit */}
              {getCurrentBranding().marketingKitUrl && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <a
                    href={getCurrentBranding().marketingKitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-white shadow-md hover:shadow-lg"
                    style={{
                      backgroundColor: getCurrentBranding().buttonMarketingKitColor || '#10B981'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonMarketingKitColor || '#10B981'}dd`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonMarketingKitColor || '#10B981'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="flex-1 font-medium text-sm">Accede al Marketing Kit</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}
              {/* Botón Guía de Viajes */}
              {getCurrentBranding().travelGuideUrl && (
                <div className="mt-2">
                  <a
                    href={getCurrentBranding().travelGuideUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-white shadow-md hover:shadow-lg"
                    style={{
                      backgroundColor: getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'}dd`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="flex-1 font-medium text-sm">Accede a la Guía de Viajes</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}
              {/* Botón Descriptivos */}
              {getCurrentBranding().descriptivesUrl && (
                <div className="mt-2">
                  <a
                    href={getCurrentBranding().descriptivesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-white shadow-md hover:shadow-lg"
                    style={{
                      backgroundColor: getCurrentBranding().buttonDescriptivesColor || '#F59E0B'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonDescriptivesColor || '#F59E0B'}dd`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonDescriptivesColor || '#F59E0B'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="flex-1 font-medium text-sm">Accede a los Descriptivos</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
          </div>
        </motion.aside>

        {/* Contenido principal */}
        <div className="flex-1 min-w-0">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header de categoría para móvil */}
            <div className="lg:hidden mb-6">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-xl rounded-lg shadow-md text-sm font-medium text-gray-700 hover:bg-white transition-all mb-4 w-full justify-between"
              >
                <div className="flex items-center gap-2">
                  <AlignLeft className="w-4 h-4" />
                  <span>{t('category.title')}</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${showMobileMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Menú desplegable móvil */}
              {showMobileMenu && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 bg-white/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/20 p-4"
                >
                  {/* Botón para volver al home */}
                  <button
                    onClick={() => {
                      onBack();
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 mb-3 text-white rounded-xl transition-all duration-200 shadow-md font-medium text-sm"
                    style={{
                      backgroundColor: getCurrentBranding().buttonBackColor || '#10B981'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonBackColor || '#10B981'}dd`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonBackColor || '#10B981'}
                  >
                    <Home className="w-4 h-4" />
                    {t('common.back')}
                  </button>

                  {/* Árbol de categorías móvil */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {categories
                      .filter(cat => !cat.parent_id)
                      .map((cat) => {
                        const catSubcategories = subcategories.filter(sub => sub.parent_id === cat.id);
                        const isCurrentCategory = cat.id === category.id;
                        
                        return (
                          <div key={cat.id} className="space-y-1">
                            <button
                              onClick={() => {
                                if (onCategoryChange) onCategoryChange(cat);
                                setShowMobileMenu(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-sm ${
                                isCurrentCategory
                                  ? 'text-white shadow-md'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              style={isCurrentCategory ? {
                                backgroundColor: getCurrentBranding().buttonCategorySelectedColor || '#3B82F6'
                              } : {}}
                            >
                              <img 
                                src={getImageUrl(cat.icon)} 
                                alt={cat.name}
                                className="w-5 h-5 rounded object-cover"
                                onError={handleImageError}
                              />
                              <span className="flex-1 font-medium">{cat.name}</span>
                              {catSubcategories.length > 0 && (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>

                            {/* Subcategorías */}
                            {isCurrentCategory && catSubcategories.length > 0 && (
                              <div className="ml-4 space-y-1 pl-3 border-l-2 border-blue-200">
                                {catSubcategories.map(sub => (
                                  <button
                                    key={sub.id}
                                    onClick={() => {
                                      setSelectedSubcategory(sub.id);
                                      setShowMobileMenu(false);
                                    }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-sm ${
                                      selectedSubcategory === sub.id
                                        ? 'bg-blue-50 text-blue-600 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="w-1.5 h-1.5 rounded-full bg-current" />
                                    <span className="flex-1">{sub.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                    {/* Botón Marketing Kit en móvil */}
                    {getCurrentBranding().marketingKitUrl && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <a
                          href={getCurrentBranding().marketingKitUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setShowMobileMenu(false)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-white shadow-md hover:shadow-lg text-sm"
                          style={{
                            backgroundColor: getCurrentBranding().buttonMarketingKitColor || '#10B981'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonMarketingKitColor || '#10B981'}dd`}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonMarketingKitColor || '#10B981'}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="flex-1 font-medium">Accede al Marketing Kit</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    )}
                    {/* Botón Guía de Viajes en móvil */}
                    {getCurrentBranding().travelGuideUrl && (
                      <div className="mt-2">
                        <a
                          href={getCurrentBranding().travelGuideUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setShowMobileMenu(false)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-white shadow-md hover:shadow-lg text-sm"
                          style={{
                            backgroundColor: getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'}dd`}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="flex-1 font-medium">Accede a la Guía de Viajes</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    )}
                    {/* Botón Descriptivos en móvil */}
                    {getCurrentBranding().descriptivesUrl && (
                      <div className="mt-2">
                        <a
                          href={getCurrentBranding().descriptivesUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setShowMobileMenu(false)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-white shadow-md hover:shadow-lg text-sm"
                          style={{
                            backgroundColor: getCurrentBranding().buttonDescriptivesColor || '#F59E0B'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonDescriptivesColor || '#F59E0B'}dd`}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonDescriptivesColor || '#F59E0B'}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="flex-1 font-medium">Accede a los Descriptivos</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
            
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4 flex-1">
                <img 
                  src={getImageUrl(category.icon || category.image)} 
                  alt={category.name}
                  className="w-16 h-16 rounded-2xl object-cover shadow-md"
                  onError={handleImageError}
                />
                <div className="flex-1">
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">{category.name}</h2>
                  <p className="text-lg lg:text-xl text-gray-600">{category.description}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-4 flex-wrap">
              <span>{sortedProducts.length} productos disponibles</span>
              <span>•</span>
              <span>Acceso exclusivo B2B</span>
              
              {/* Botón Reserva de Hotel - Solo visible en categoría Hoteles */}
              {getCurrentBranding().hotelButtonUrl && category.name.toLowerCase().includes('hotel') && (
                <>
                  <span>•</span>
                  <a
                    href={getCurrentBranding().hotelButtonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 text-white shadow-md hover:shadow-lg font-semibold text-sm"
                    style={{
                      backgroundColor: getCurrentBranding().buttonHotelColor || '#F59E0B'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonHotelColor || '#F59E0B'}dd`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonHotelColor || '#F59E0B'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="uppercase tracking-wide">RESERVA TU HOTEL AQUÍ</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </>
              )}
            </div>
          </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('common.search')}
                className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              >
                <option value="order">Ordenar por posición</option>
                <option value="name">Ordenar por nombre</option>
                <option value="lastUpdated">Ordenar por última actualización</option>
              </select>
              
              <button className="flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">{t('common.filter')}</span>
              </button>
              
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('detailed')}
                  className={`p-2 rounded-lg transition-all ${
                    viewMode === 'detailed' 
                      ? 'bg-white shadow-sm text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Lista Detallada"
                >
                  <AlignLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-white shadow-sm text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Cuadrícula"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${
                    viewMode === 'list' 
                      ? 'bg-white shadow-sm text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Lista"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : viewMode === 'detailed'
              ? 'grid-cols-1'
              : 'grid-cols-1 md:grid-cols-2'
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {sortedProducts.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              index={index}
              viewMode={viewMode}
            />
          ))}
        </motion.div>

        {sortedProducts.length === 0 && (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {t('messages.noResults')}
            </h3>
            <p className="text-gray-500">
              Prueba con otros términos relacionados a hoteles, tours o experiencias.
            </p>
          </motion.div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;